import { DrumType } from "../Composers/DrumPatterns";
import { MusicalNote } from "../MusicalNote";
import { CrashCymbal } from "./CrashCymbal";
import { ElectroTom } from "./ElectroTom";
import { HiHat } from "./HiHat";
import { Instrument } from "./Instrument";
import { InstrumentParameter } from "./InstrumentParameter";
import { KickDrum } from "./KickDrum";
import { SnareDrum } from "./SnareDrum";

/**
 * Composite drums instrument that routes to individual drum instruments.
 *
 * Routes drum notes to appropriate child instruments based on MIDI note numbers.
 * Each drum type (kick, snare, hi-hat) is handled by its own specialized
 * instrument with dedicated parameters.
 *
 * Drum mapping:
 * - Note 36 (C2): Kick drum
 * - Note 38 (D2): Snare drum
 * - Note 42 (F#2): Closed hi-hat
 * - Note 46 (A#2): Open hi-hat
 * - Note 49 (C#3): Crash cymbal
 * - Note 41 (F2): Low tom
 * - Note 43 (G2): Mid tom
 * - Note 45 (A2): High tom
 */
export class DrumsInstrument extends Instrument {
  // Child instruments
  private kickDrum: KickDrum;
  private snareDrum: SnareDrum;
  private hiHat: HiHat;
  private crashCymbal: CrashCymbal;
  private lowTom: ElectroTom;
  private midTom: ElectroTom;
  private highTom: ElectroTom;

  constructor(audioContext: AudioContext, output: GainNode) {
    super(audioContext, output);

    // Create child instruments
    this.kickDrum = new KickDrum(audioContext, this.gain);
    this.snareDrum = new SnareDrum(audioContext, this.gain);
    this.hiHat = new HiHat(audioContext, this.gain);
    this.crashCymbal = new CrashCymbal(audioContext, this.gain);

    // Create three tom instances with different pitches
    this.lowTom = new ElectroTom(audioContext, this.gain, 41); // F2
    this.midTom = new ElectroTom(audioContext, this.gain, 43); // G2
    this.highTom = new ElectroTom(audioContext, this.gain, 45); // A2

    // Set default volumes for child instruments
    this.kickDrum.setVolume(1.5);
    this.snareDrum.setVolume(0.8);
    this.hiHat.setVolume(0.4);
    this.crashCymbal.setVolume(0.6);
    this.lowTom.setVolume(0.7);
    this.midTom.setVolume(0.7);
    this.highTom.setVolume(0.7);
  }

  /**
   * Play a drum sound by routing to appropriate child instrument.
   */
  playNote(note: MusicalNote, startTime: number, duration: number): void {
    const drumType = this.getDrumTypeFromNote(note.noteNumber);

    // Route to appropriate child instrument
    switch (drumType) {
      case "kick":
        this.kickDrum.playNote(note, startTime, duration);
        break;
      case "snare":
        this.snareDrum.playNote(note, startTime, duration);
        break;
      case "hihat_closed":
      case "hihat_open":
        // Both open and closed hi-hats use the same HiHat instrument
        // The instrument determines open/closed based on note number
        this.hiHat.playNote(note, startTime, duration);
        break;
      case "crash":
        this.crashCymbal.playNote(note, startTime, duration);
        break;
      case "tom_low":
        this.lowTom.playNote(note, startTime, duration);
        break;
      case "tom_mid":
        this.midTom.playNote(note, startTime, duration);
        break;
      case "tom_high":
        this.highTom.playNote(note, startTime, duration);
        break;
      default:
        console.warn(`Unknown drum type for note ${note.noteNumber}`);
    }
  }

  /**
   * Map MIDI note numbers to drum types.
   */
  private getDrumTypeFromNote(noteNumber: number): DrumType {
    switch (noteNumber) {
      case 36: // C2
        return "kick";
      case 38: // D2
        return "snare";
      case 42: // F#2
        return "hihat_closed";
      case 46: // A#2
        return "hihat_open";
      case 49: // C#3
        return "crash";
      case 41: // F2
        return "tom_low";
      case 43: // G2
        return "tom_mid";
      case 45: // A2
        return "tom_high";
      default:
        // Default to kick for unknown notes
        return "kick";
    }
  }

  /**
   * Override getParameters to aggregate parameters from all child instruments.
   * This allows the UI to control all drum parameters from a single interface.
   */
  getParameters(): InstrumentParameter[] {
    const baseParameters = super.getParameters();
    const kickParameters = this.kickDrum.getParameters();
    const snareParameters = this.snareDrum.getParameters();
    const hihatParameters = this.hiHat.getParameters();
    const crashParameters = this.crashCymbal.getParameters();
    const lowTomParameters = this.lowTom.getParameters();
    const midTomParameters = this.midTom.getParameters();
    const highTomParameters = this.highTom.getParameters();

    // Prefix child parameters to avoid key conflicts
    const prefixedKickParams = kickParameters.map((param) => {
      return new InstrumentParameter(
        () => param.getValue(),
        (value) => param.setValue(value),
        {
          key: `kick_${param.key}`,
          label: param.label,
          min: param.min,
          max: param.max,
          defaultValue: param.defaultValue,
          step: param.step,
          unit: param.unit,
          category: "Kick",
        },
      );
    });

    const prefixedSnareParams = snareParameters.map((param) => {
      return new InstrumentParameter(
        () => param.getValue(),
        (value) => param.setValue(value),
        {
          key: `snare_${param.key}`,
          label: param.label,
          min: param.min,
          max: param.max,
          defaultValue: param.defaultValue,
          step: param.step,
          unit: param.unit,
          category: "Snare",
        },
      );
    });

    const prefixedHihatParams = hihatParameters.map((param) => {
      return new InstrumentParameter(
        () => param.getValue(),
        (value) => param.setValue(value),
        {
          key: `hihat_${param.key}`,
          label: param.label,
          min: param.min,
          max: param.max,
          defaultValue: param.defaultValue,
          step: param.step,
          unit: param.unit,
          category: "Hi-Hat",
        },
      );
    });

    const prefixedCrashParams = crashParameters.map((param) => {
      return new InstrumentParameter(
        () => param.getValue(),
        (value) => param.setValue(value),
        {
          key: `crash_${param.key}`,
          label: param.label,
          min: param.min,
          max: param.max,
          defaultValue: param.defaultValue,
          step: param.step,
          unit: param.unit,
          category: "Crash",
        },
      );
    });

    const prefixedLowTomParams = lowTomParameters.map((param) => {
      return new InstrumentParameter(
        () => param.getValue(),
        (value) => param.setValue(value),
        {
          key: `lowTom_${param.key}`,
          label: param.label,
          min: param.min,
          max: param.max,
          defaultValue: param.defaultValue,
          step: param.step,
          unit: param.unit,
          category: "Low Tom",
        },
      );
    });

    const prefixedMidTomParams = midTomParameters.map((param) => {
      return new InstrumentParameter(
        () => param.getValue(),
        (value) => param.setValue(value),
        {
          key: `midTom_${param.key}`,
          label: param.label,
          min: param.min,
          max: param.max,
          defaultValue: param.defaultValue,
          step: param.step,
          unit: param.unit,
          category: "Mid Tom",
        },
      );
    });

    const prefixedHighTomParams = highTomParameters.map((param) => {
      return new InstrumentParameter(
        () => param.getValue(),
        (value) => param.setValue(value),
        {
          key: `highTom_${param.key}`,
          label: param.label,
          min: param.min,
          max: param.max,
          defaultValue: param.defaultValue,
          step: param.step,
          unit: param.unit,
          category: "High Tom",
        },
      );
    });

    return [
      ...baseParameters,
      ...prefixedKickParams,
      ...prefixedSnareParams,
      ...prefixedHihatParams,
      ...prefixedCrashParams,
      ...prefixedLowTomParams,
      ...prefixedMidTomParams,
      ...prefixedHighTomParams,
    ];
  }

  /**
   * Get all valid MIDI notes for the drum kit.
   */
  getValidNotes(): number[] {
    // Combine valid notes from all child instruments
    const kickNotes = this.kickDrum.getValidNotes();
    const snareNotes = this.snareDrum.getValidNotes();
    const hihatNotes = this.hiHat.getValidNotes();
    const crashNotes = this.crashCymbal.getValidNotes();
    const lowTomNotes = this.lowTom.getValidNotes();
    const midTomNotes = this.midTom.getValidNotes();
    const highTomNotes = this.highTom.getValidNotes();

    // Merge and sort all valid notes
    const allNotes = [
      ...kickNotes,
      ...snareNotes,
      ...hihatNotes,
      ...crashNotes,
      ...lowTomNotes,
      ...midTomNotes,
      ...highTomNotes,
    ];
    return [...new Set(allNotes)].sort((a, b) => a - b);
  }

  /**
   * Clean up resources including child instruments.
   */
  onDestroy(): void {
    super.onDestroy();
    this.kickDrum.onDestroy();
    this.snareDrum.onDestroy();
    this.hiHat.onDestroy();
    this.crashCymbal.onDestroy();
    this.lowTom.onDestroy();
    this.midTom.onDestroy();
    this.highTom.onDestroy();
  }
}
